/* eslint-disable import/no-unresolved */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import ReportMastercardImage from 'assets/images/reportMastercard.png';
import ReportVisaImage from 'assets/images/reportVisacard.svg';
import ReportGalileoVisaImage from 'assets/images/card-galileo-visa.png';
import ReportGalileoMastercardImage from 'assets/images/card-galileo-mastercard.png';
import { CardSettingsContext } from '../../../../contexts/card/settings/context';
import { REPORT_CARD_STATUS } from '../../../../utilities/constants';
import {
  CARD_NETWORKS,
  cardProviders,
  mainCardTypes,
  ReportCardScreensFlow,
} from '../../constants';
import {
  CARD_GALILEO_REPORT_LOST,
  CARD_GALILEO_REPORT_STOLEN,
  CARD_MONAVATE_REPORT_LOST,
  CARD_MONAVATE_REPORT_STOLEN,
} from '../../mutations';
import { showAlert } from '../../../../utilities/notifications';
import MainBodyLayout from '../partials/MainBodyLayout';
import { cardSettingsScreens } from '../constants';
import { GenericLoading } from '../../../../components/Placeholders';

const ConfirmReportCard = () => {
  const { t: translate } = useTranslation();
  const { state, openScreen } = useContext(CardSettingsContext);
  const history = useHistory();

  const screenOptions = state?.screenOptions;
  const cardProvider = state?.user?.cardProvider;
  const cardStatus = state?.cardStatus;

  const reportCardKey = useMemo(() => {
    if (screenOptions && screenOptions.state === REPORT_CARD_STATUS.STOLEN) {
      return CARD_MONAVATE_REPORT_STOLEN;
    }
    if (screenOptions && screenOptions.state === REPORT_CARD_STATUS.LOST) {
      return CARD_MONAVATE_REPORT_LOST;
    }
    return null;
  }, [screenOptions]);

  const isMonavateCard = useMemo(() => cardProvider === cardProviders.MONAVATE, [cardProvider]);

  const reportGalileoCardKey = useMemo(() => {
    if (screenOptions) {
      if (screenOptions.state === REPORT_CARD_STATUS.STOLEN) {
        return CARD_GALILEO_REPORT_STOLEN;
      }
      if (screenOptions.state === REPORT_CARD_STATUS.LOST) {
        return CARD_GALILEO_REPORT_LOST;
      }
    }

    return CARD_GALILEO_REPORT_LOST;
  }, [screenOptions]);

  const { mutate: reportMastercardCard } = useMutation(reportCardKey, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        openScreen(ReportCardScreensFlow.ORDER_REPLACEMENT_CARD, {
          ...screenOptions,
          success: data.success,
          withBack: false,
        });
      }
    },
  });

  const { mutate: reportGalileoCard } = useMutation(reportGalileoCardKey, {
    onSettled: (data, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        openScreen(cardSettingsScreens.LAST_FIVE_TRANSACTIONS);
      }
    },
  });

  const handleReportCard = () => {
    if (isMonavateCard) {
      history.push('/dashboard/card/order-card', {
        isCardReplacement: true,
        mainCardType: mainCardTypes.PHYSICAL,
        cardIssueOptions: { ...screenOptions },
      });
    } else {
      reportMastercardCard();
    }
  };

  const handleNotOrderCard = () => {
    reportGalileoCard({
      order_replacement: false,
      use_expedited_shipping: false,
    });
  };

  const handleOrderCard = () => {
    openScreen(cardSettingsScreens.CONFIRM_REPLACEMENT_CARD_DETAILS, {
      ...screenOptions,
    });
  };

  const handleCancelReport = () => {
    history.push('/dashboard/card/details');
  };

  const [isLoadedImage, setIsLoadedImage] = useState(false);
  const [imageSrc, setImageSrc] = useState('');

  useEffect(() => {
    const img = new Image();

    if (cardProvider === cardProviders.GALILEO) {
      if (cardStatus?.network?.toUpperCase() === CARD_NETWORKS.MASTERCARD) {
        img.src = ReportGalileoMastercardImage;
      } else {
        img.src = ReportGalileoVisaImage;
      }
    } else if (cardProvider === cardProviders.MONAVATE) {
      img.src = ReportMastercardImage;
    } else {
      img.src = ReportVisaImage;
    }
    setImageSrc(img.src);
    img.onload = () => setIsLoadedImage(true);
  }, [cardProvider, cardStatus]);

  const galileoWarningLabel = useMemo(() => {
    if (
      screenOptions.state === REPORT_CARD_STATUS.LOST ||
      screenOptions.state === REPORT_CARD_STATUS.STOLEN
    ) {
      return translate('order_replacement_card_galileo_lost_stolen_info_1');
    }
    return translate('order_replacement_card_galileo_damaged_info_1');
  }, [screenOptions?.state, translate]);

  const galileoWarningsublabel = useMemo(() => {
    if (
      screenOptions.state === REPORT_CARD_STATUS.LOST ||
      screenOptions.state === REPORT_CARD_STATUS.STOLEN
    ) {
      return translate('order_replacement_card_galileo_lost_stolen_info_2');
    }
    return translate('order_replacement_card_galileo_damaged_info_2');
  }, [screenOptions?.state, translate]);

  if (!isLoadedImage) {
    return <GenericLoading />;
  }

  return (
    <MainBodyLayout
      withBack
      onPrevClick={() => openScreen(cardSettingsScreens.REPORT_CARD_ISSUE)}
      title={
        cardProvider === cardProviders.GALILEO
          ? translate('replacement_card')
          : translate('manage_card_report_card_issue')
      }>
      {cardProvider !== cardProviders.GALILEO && (
        <div className="text-f5  mt-4 mb-4 ">
          {isMonavateCard
            ? translate('monavate_report_card_issue_confirm')
            : translate('report_card_issue_confirm')}
        </div>
      )}
      <img
        style={{
          height: 'auto',
          marginTop: '1rem',
          resize: 'contain',
          alignSelf: 'center',
          maxWidth: '31rem',
          width: cardProvider === cardProviders.GALILEO ? '25rem' : '100%',
          paddingBottom: cardProvider === cardProviders.GALILEO ? '4rem' : '0px',
        }}
        src={imageSrc}
        loading="lazy"
        alt="masterCardSecurity"
      />
      {cardProvider === cardProviders.GALILEO && (
        <div className="text-f5 flex-column d-flex align-items-center justify-content-center">
          <p className="text-center">{galileoWarningLabel}</p>
          <p className="text-shadow mt-2 text-center">{galileoWarningsublabel}</p>
        </div>
      )}
      <div className="btn-container">
        {cardProvider === cardProviders.GALILEO && (
          <div className="d-flex gap-4 w-100">
            {(screenOptions.state === REPORT_CARD_STATUS.LOST ||
              screenOptions.state === REPORT_CARD_STATUS.STOLEN) && (
              <button
                onClick={handleNotOrderCard}
                type="button"
                className="btn py-3 border-grey border w-100">
                {translate('order_replacement_card_galileo_lost_stolen_btn_block_card')}
              </button>
            )}
            <button onClick={handleOrderCard} type="button" className="btn py-3 btn-primary w-100">
              {translate('order_replacement_card_galileo_btn_block_order_card')}
            </button>
          </div>
        )}
        {cardProvider !== cardProviders.GALILEO && (
          <div className="d-flex gap-4 w-100">
            <button
              onClick={handleCancelReport}
              type="button"
              className="btn py-3 border-grey border w-100">
              {translate('cancel_report')}
            </button>
            <button onClick={handleReportCard} type="button" className="btn py-3 btn-primary w-100">
              {isMonavateCard ? translate('confirm_button_label') : translate('confirm_report')}
            </button>
          </div>
        )}
      </div>
    </MainBodyLayout>
  );
};

export default ConfirmReportCard;
